import React from "react";
import { User, Bot, Clock, CheckCircle } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Card } from "../../ui/card";
import { getStatusV } from "../../../utils/helper";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { useSelector } from "react-redux";
import {
  getProfileForm,
  getConversationalStatus,
} from "../../../redux/reducer/incbotFeedbackReducer";

const SpecificQuestion = ({
  key,
  timeTaken,
  AITextRating,
  questionNumber,
  questionText,
  transcript,
  responseAnalysis,
  rating,
  conversation = [],
  isGeneral,
  codeBlock,
  predefinedAnswer,
}: any) => {
  const profileForm = useSelector(getProfileForm);
  const conversationStatus = useSelector(getConversationalStatus);

  return (
    <Card key={key} className="rounded-lg shadow-sm mb-4 md:mb-6">
      <div className="p-3 md:p-4 lg:p-6 bg-white">
        <div className="flex items-center justify-between mb-4 gap-1">
          <div className="flex items-start gap-2">
            <span className="text-xs md:text-lg font-medium">{questionNumber}.</span>
            <span className="text-xs md:text-lg whitespace-pre-wrap">{questionText}</span>
          </div>
          {!isGeneral && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <CheckCircle className={`text-${getStatusV(rating).color}-500`} size={18} />
              <Badge
                variant="outline"
                className={`text-${getStatusV(rating).color}-700 border-${
                  getStatusV(rating).color
                }-200 bg-${getStatusV(rating).color}-50 text-xs lg:text-sm`}
              >
                {`${rating * 10}% Correct`}
              </Badge>
            </div>
          )}
        </div>

        {codeBlock && (
          <div className="mb-4">
            <div className="text-xs md:text-sm font-medium text-gray-700 mb-2">Code Block:</div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs md:text-sm text-gray-700 font-mono">
              <pre className="whitespace-pre-wrap">{codeBlock}</pre>
            </div>
          </div>
        )}

        {predefinedAnswer && (
          <div className="mb-4">
            <div className="text-xs md:text-sm font-medium text-gray-700 mb-2">
              Expected Answer:
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs md:text-sm text-gray-700">
              <pre className="whitespace-pre-wrap">{predefinedAnswer}</pre>
            </div>
          </div>
        )}

        <div className="mb-2 md:mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs md:text-sm font-medium text-gray-700">Transcript:</span>
            {!conversationStatus && (
              <div className="flex items-center gap-1">
                <Clock size={12} />
                <span className="text-xs text-gray-500">{timeTaken}s</span>
              </div>
            )}
            {AITextRating &&
              !isGeneral &&
              ("rating" in AITextRating && AITextRating.rating ? (
                <Badge
                  variant="outline"
                  className="text-orange-700 border-orange-200 bg-orange-50 text-[10px] md:text-xs"
                >
                  AI-Assisted Response
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className={`text-green-700 border-green-200 bg-green-50 text-[10px] md:text-xs ${
                    transcript && transcript?.trim() !== "The candidate skipped the question."
                      ? ""
                      : "opacity-0"
                  }`}
                >
                  100% Human Response
                </Badge>
              ))}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs md:text-sm text-gray-700 break-words">
            {transcript}
          </div>
        </div>

        {!isGeneral && (
          <div className="mb-4">
            <div className="text-xs md:text-sm font-medium text-gray-700 mb-2">
              Response Analysis:
            </div>
            <div className="bg-green-50 border-l-4 border-green-400 p-3 text-xs md:text-sm text-gray-700">
              {responseAnalysis || "Not Available"}
            </div>
          </div>
        )}

        {conversation.length > 0 && (
          <div className="mb-4">
            <div className="text-xs md:text-sm font-medium text-gray-700 mb-3">
              Dynamic Follow ups:
            </div>
            <div className="space-y-4">
              {conversation.map((message: any, index: number) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.isCandidate ? "justify-end" : "justify-start"}`}
                >
                  {!message.isCandidate && (
                    <div className="flex-shrink-0">
                      <Avatar className="w-6 h-6 md:w-12 md:h-12">
                        {/* <AvatarImage src="/lovable-uploads/709e1dda-5a0f-44c0-ac0f-c435a9375740.png" alt="Response Analysis" /> */}
                        <AvatarFallback className="bg-blue-100 text-blue-600 max-md:text-xs">
                          IB
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                  <div
                    className={`max-w-3xl min-w-0 break-words ${
                      message.isCandidate
                        ? "bg-gray-100  border border-grey-200 rounded-tl-lg p-2"
                        : "bg-green-50 border border-green-200 p-3 rounded-tr-lg"
                    } rounded-b-lg md:p-4 shadow-sm`}
                  >
                    {message.type === "question" && (
                      <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Follow Up Question
                      </div>
                    )}
                    <div className={message.isCandidate ? "bg-blue-50 rounded-md p-2" : ""}>
                      {message.type === "transcript" && (
                        <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">
                          Transcript
                        </div>
                      )}
                      <div className="text-xs md:text-sm text-gray-700">{message.content}</div>
                    </div>
                    {message?.aiResponse && !isGeneral && (
                      <div className={message.isCandidate ? "bg-green-50 rounded-md p-2 mt-2" : ""}>
                        {message.type === "transcript" && (
                          <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Response Analysis
                          </div>
                        )}
                        <div className="text-xs md:text-sm text-gray-700 msx-md:text-justify">
                          {message.aiResponse}
                        </div>
                      </div>
                    )}
                  </div>
                  {message.isCandidate && (
                    <div className="flex-shrink-0">
                      <Avatar className="w-6 h-6 md:w-12 md:h-12">
                        <AvatarImage src={profileForm?.candidateImage} alt="Response Analysis" />
                        <AvatarFallback className="bg-gray-600 text-white">
                          {profileForm?.name
                            .split(" ")
                            .map((n: any) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SpecificQuestion;
